<template>
  <section class="manifest-detail-page">
    <div v-if="isLoading" class="manifest-loading-state text-center py-5">
      <b-spinner variant="primary" class="mb-1" />
      <p class="text-muted mb-0">Chargement du manifeste pickup...</p>
    </div>

    <b-alert v-else-if="!manifest" variant="danger" show>
      <h4 class="alert-heading">Manifeste introuvable</h4>
      <div class="alert-body mb-0">
        Impossible de charger le détail de ce manifeste pickup.
      </div>
    </b-alert>

    <div v-else>
      <div class="manifest-screen-content">
      <div class="manifest-page-header mb-3">
        <div>
          <div class="manifest-eyebrow">Manifests Pickup</div>
          <div class="d-flex flex-wrap align-items-center gap-1 mb-50">
            <h2 class="manifest-title mb-0">Manifeste #{{ manifest.id }}</h2>
            <b-badge variant="light-primary" pill class="manifest-pill">
              {{ manifestOrders.length }} colis
            </b-badge>
          </div>
          <p class="text-muted mb-0">
            Créé le {{ formatDate(manifest.created_at) }}
          </p>
        </div>

        <div class="manifest-header-actions">
          <b-button variant="outline-primary" @click="printManifest">
            <feather-icon icon="PrinterIcon" class="mr-50" />
            Imprimer
          </b-button>
          <b-button variant="outline-secondary" :to="{ name: 'manifests-list' }">
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            Retour à la liste
          </b-button>
        </div>
      </div>

      <b-row class="mb-2">
        <b-col md="4" sm="6" class="mb-1">
          <div class="manifest-stat-card stat-company h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="BriefcaseIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label">Société sous-traitance</div>
              <div class="manifest-stat-value">{{ outsourcingCompanyName }}</div>
            </div>
          </div>
        </b-col>

        <b-col md="4" sm="6" class="mb-1">
          <div class="manifest-stat-card stat-user h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="UserIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label">Créé par</div>
              <div class="manifest-stat-value">{{ createdByLabel }}</div>
              <small class="text-muted">{{ createdByRole }}</small>
            </div>
          </div>
        </b-col>

        <b-col md="4" sm="12" class="mb-1">
          <div class="manifest-stat-card stat-count h-100">
            <div class="manifest-stat-icon">
              <feather-icon icon="PackageIcon" size="18" />
            </div>
            <div>
              <div class="manifest-stat-label">Nombre de colis</div>
              <div class="manifest-stat-value">{{ manifest.orders_count }}</div>
              <small class="text-muted">Dans ce manifeste</small>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col lg="8" class="mb-2 mb-lg-0">
          <b-card class="manifest-section-card h-100" no-body>
            <b-card-body>
              <div class="section-title-row mb-1">
                <div>
                  <h5 class="mb-25">Informations générales</h5>
                  <p class="text-muted mb-0">Vue rapide du manifeste et de son affectation.</p>
                </div>
              </div>

              <div class="manifest-meta-grid">
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Référence</span>
                  <span class="manifest-meta-value">#{{ manifest.id }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Chauffeur</span>
                  <span class="manifest-meta-value">{{ driverLabel }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Véhicule</span>
                  <span class="manifest-meta-value">{{ manifest.car || '-' }}</span>
                </div>
                <div class="manifest-meta-item">
                  <span class="manifest-meta-label">Dernière mise à jour</span>
                  <span class="manifest-meta-value">{{ formatDate(manifest.updated_at) }}</span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col lg="4">
          <b-card class="manifest-section-card h-100" no-body>
            <b-card-body>
              <h5 class="mb-25">Note</h5>
              <p v-if="manifest.note" class="manifest-note mb-0">
                {{ manifest.note }}
              </p>
              <p v-else class="text-muted mb-0">
                Aucune note ajoutée pour ce manifeste.
              </p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-card class="manifest-section-card" no-body>
        <b-card-body class="pb-0">
          <div class="section-title-row mb-2">
            <div>
              <h5 class="mb-25">Colis dans ce manifeste</h5>
              <p class="text-muted mb-0">Consultez, recherchez et gérez les colis déjà affectés.</p>
            </div>
            <b-badge variant="light-primary" pill>
              {{ manifestOrders.length }} ligne<span v-if="manifestOrders.length > 1">s</span>
            </b-badge>
          </div>
        </b-card-body>

        <div class="px-1 pb-1">
          <div v-if="manifestOrders.length" class="manifest-table-shell">
            <vue-good-table
              :columns="orderColumns"
              :rows="manifestOrders"
              :search-options="{ enabled: true, placeholder: 'Rechercher un colis dans ce manifeste' }"
              :pagination-options="{ enabled: true, perPage: 10 }"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'added_by'">
                  <span class="font-weight-bolder d-block">{{ props.row.added_by && props.row.added_by.username }}</span>
                  <small class="text-muted">{{ formatDate(props.row.added_at) }}</small>
                </span>
                <span v-else-if="props.column.field === 'order_price_ttc'">
                  <span class="font-weight-bolder">{{ formatAmount(props.row.order_price_ttc) }}</span>
                </span>
                <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
              </template>
            </vue-good-table>
          </div>

          <div v-else class="manifest-empty-state manifest-empty-state-large">
            <feather-icon icon="PackageIcon" size="24" class="mb-50 text-muted" />
            <h6 class="mb-50">Aucun colis dans ce manifeste</h6>
            <p class="text-muted mb-0">
              Ce manifeste ne contient encore aucun colis.
            </p>
          </div>
        </div>
      </b-card>
      </div>

      <section class="manifest-print-wrapper" aria-label="Impression bon de sous-traitance pickup">
        <div class="manifest-print-logo">
          <img src="/logo.png" alt="Logo de la société">
        </div>

        <div class="manifest-print-title-row">
          <h1>Bon de sous-traitance pickup N° : {{ manifest.id }}</h1>
          <h2>Date : {{ formatPrintDate(manifest.created_at) }}</h2>
        </div>

        <div class="manifest-print-info-row">
          <div class="manifest-print-info-card">
            <div class="manifest-print-info-line">
              <strong>{{ deliveryCompany.name || '-' }}</strong> : {{ deliveryCompany.tagline || 'Société de livraison des colis' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Matricule fiscale :</strong> {{ deliveryCompany.tax_identification_number || '-' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Adresse :</strong> {{ deliveryCompany.address || '-' }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Tel :</strong> {{ deliveryCompany.phone1 || deliveryCompany.phone || '-' }}
            </div>
          </div>

          <div class="manifest-print-info-card">
            <div class="manifest-print-info-line">
              <strong>Société sous-traitance :</strong> {{ outsourcingCompanyName }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Matricule fiscale :</strong> {{ outsourcingCompanyTaxId }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Adresse :</strong> {{ outsourcingCompanyAddress }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Tel :</strong> {{ outsourcingCompanyPhone }}
            </div>
            <div class="manifest-print-info-line">
              <strong>Nombre des colis :</strong> {{ manifestOrders.length }}
            </div>
          </div>
        </div>

        <table class="manifest-print-table">
          <thead>
            <tr>
              <th>BL</th>
              <th>Destinataire</th>
              <th>Adresse</th>
              <th>Prix</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in manifestOrders" :key="order.id">
              <td>{{ order.order_id }}</td>
              <td>
                <strong>{{ order.order_name || '-' }}</strong>
              </td>
              <td>
                <strong>{{ order.order_governorate || '-' }}</strong>
                <span>{{ order.order_address || '-' }}</span>
              </td>
              <td>{{ formatAmount(order.order_price_ttc) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="manifest-print-footer">
          <h3>Signature</h3>
          <h2>Total : {{ formatAmount(cashOnDeliveryTotal) }}</h2>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import storeAuth from '@/store/store'

export default {
  name: 'ManifestShow',
  components: { VueGoodTable },
  props: {
    id: { type: [String, Number], required: true },
  },
  computed: {
    ...mapGetters('manifestsPickup', ['manifest', 'isLoading']),
    manifestOrders() {
      return this.manifest?.manifest_orders || []
    },
    deliveryCompany() {
      return storeAuth.state.company || {}
    },
    cashOnDeliveryTotal() {
      return this.sumOrderAmounts(this.manifestOrders)
    },
    outsourcingCompanyName() {
      return this.manifest?.outsourcing_company?.display_name || this.manifest?.outsourcing_company?.name || '-'
    },
    outsourcingCompanyAddress() {
      return this.manifest?.outsourcing_company?.address || '-'
    },
    outsourcingCompanyPhone() {
      return this.manifest?.outsourcing_company?.phone || this.manifest?.outsourcing_company?.phone2 || '-'
    },
    outsourcingCompanyTaxId() {
      return this.manifest?.outsourcing_company?.tax_identification_number || '-'
    },
    createdByLabel() {
      return this.manifest?.created_by?.username || '-'
    },
    createdByRole() {
      return this.manifest?.created_by?.role || '-'
    },
    driverLabel() {
      return this.manifest?.driver?.username || '-'
    },
    orderColumns() {
      return [
        { label: 'Référence', field: 'order_id' },
        { label: 'Client', field: 'order_name' },
        { label: 'Téléphone', field: 'order_phone1' },
        { label: 'Gouvernorat', field: 'order_governorate' },
        { label: 'Adresse', field: 'order_address' },
        { label: 'Prix TTC', field: 'order_price_ttc', type: 'number' },
        { label: 'Ajouté par / le', field: 'added_by' },
      ]
    },
  },
  created() {
    this.fetchManifest(this.id)
  },
  methods: {
    ...mapActions('manifestsPickup', ['fetchManifest']),
    sumOrderAmounts(orders) {
      return orders.reduce((sum, order) => {
        const amount = Number.parseFloat(order.order_price_ttc)
        return Number.isNaN(amount) ? sum : sum + amount
      }, 0)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('fr-TN')
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `${value} DT`
    },
    formatPrintDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      return date.toISOString().slice(0, 10)
    },
    printManifest() {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => { window.print() })
      } else {
        window.print()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style scoped>
.manifest-detail-page {
  padding-bottom: 1rem;
}

.manifest-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #fffaf2 0%, #ffffff 55%, #f3f7ff 100%);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.manifest-eyebrow {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a6d3b;
  margin-bottom: 0.35rem;
}

.manifest-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #223047;
}

.manifest-pill {
  font-size: 0.8rem;
}

.manifest-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.manifest-print-wrapper {
  display: none;
}

.manifest-stat-card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 0.9rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.manifest-stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.75);
}

.stat-company .manifest-stat-icon {
  color: #c27c2c;
  background: #fff3df;
}

.stat-user .manifest-stat-icon {
  color: #0f766e;
  background: #ddfbf5;
}

.stat-count .manifest-stat-icon {
  color: #3657d6;
  background: #e8efff;
}

.manifest-stat-label,
.manifest-meta-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #7b8798;
  margin-bottom: 0.2rem;
}

.manifest-stat-value,
.manifest-meta-value {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: #223047;
}

.manifest-section-card {
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 1rem;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.manifest-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.manifest-meta-item {
  padding: 0.9rem 1rem;
  border-radius: 0.85rem;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.manifest-note {
  line-height: 1.7;
  color: #344054;
}

.manifest-table-shell {
  border-radius: 0.9rem;
  overflow: hidden;
}

.manifest-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1.5rem;
  text-align: center;
  background: #fafbfd;
  border: 1px dashed #d8e0ea;
  border-radius: 0.9rem;
}

.manifest-empty-state-large {
  min-height: 220px;
  margin: 0 1rem 1rem;
}

.manifest-loading-state {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.manifest-action-btn {
  border-radius: 0.6rem;
}

.gap-1 {
  gap: 0.75rem;
}

@media (max-width: 991.98px) {
  .manifest-page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .manifest-header-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 767.98px) {
  .manifest-meta-grid {
    grid-template-columns: 1fr;
  }

  .manifest-title {
    font-size: 1.5rem;
  }
}
</style>

<style>
@media print {
  @page {
    size: A2 landscape;
    margin: 0;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle,
  .Vue-Toastification__container,
  .Vue-Toastification__toast,
  .manifest-screen-content {
    display: none !important;
  }

  .content.app-content,
  .app-content,
  .content,
  .content-wrapper,
  .content-body {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    background: #ffffff !important;
  }

  .manifest-detail-page {
    padding: 0 !important;
    margin: 0 !important;
    background: #ffffff !important;
  }

  .manifest-print-wrapper {
    display: block !important;
    padding: 10mm 13mm 8mm !important;
    color: #000000 !important;
    font-family: Arial, Helvetica, sans-serif !important;
    background: #ffffff !important;
  }

  .manifest-print-logo {
    display: flex !important;
    justify-content: flex-start !important;
    margin-bottom: 6mm !important;
  }

  .manifest-print-logo img {
    width: 190px !important;
    max-width: 190px !important;
    height: auto !important;
  }

  .manifest-print-title-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 16px !important;
    margin-bottom: 8mm !important;
  }

  .manifest-print-title-row h1,
  .manifest-print-title-row h2 {
    margin: 0 !important;
    color: #000000 !important;
    font-weight: 800 !important;
  }

  .manifest-print-title-row h1 {
    font-size: 27px !important;
  }

  .manifest-print-title-row h2 {
    font-size: 19px !important;
    padding-top: 5px !important;
  }

  .manifest-print-info-row {
    display: flex !important;
    justify-content: space-between !important;
    gap: 18px !important;
    margin-bottom: 8mm !important;
  }

  .manifest-print-info-card {
    flex: 1 1 0 !important;
    min-height: 88px !important;
    padding: 11px 13px !important;
    border: 2px solid #000000 !important;
    background: #ffffff !important;
    color: #000000 !important;
  }

  .manifest-print-info-line {
    margin-bottom: 4px !important;
    color: #000000 !important;
    font-size: 18px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
  }

  .manifest-print-info-line:last-child {
    margin-bottom: 0 !important;
  }

  .manifest-print-info-line strong {
    color: #000000 !important;
    font-weight: 900 !important;
  }

  .manifest-print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    color: #000000 !important;
    table-layout: fixed !important;
  }

  .manifest-print-table th,
  .manifest-print-table td {
    border: 1px solid #000000 !important;
    color: #000000 !important;
    vertical-align: middle !important;
    text-align: center !important;
    word-break: break-word !important;
  }

  .manifest-print-table th {
    padding: 7px 8px !important;
    font-size: 13px !important;
    font-weight: 900 !important;
    text-transform: uppercase !important;
    background: #f0f0f4 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .manifest-print-table td {
    padding: 8px 8px !important;
    font-size: 18px !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
  }

  .manifest-print-table th:nth-child(1),
  .manifest-print-table td:nth-child(1) {
    width: 16% !important;
  }

  .manifest-print-table th:nth-child(2),
  .manifest-print-table td:nth-child(2) {
    width: 27% !important;
  }

  .manifest-print-table th:nth-child(3),
  .manifest-print-table td:nth-child(3) {
    width: 42% !important;
  }

  .manifest-print-table th:nth-child(4),
  .manifest-print-table td:nth-child(4) {
    width: 15% !important;
    white-space: nowrap !important;
  }

  .manifest-print-table td span,
  .manifest-print-table td strong {
    display: block !important;
    color: #000000 !important;
  }

  .manifest-print-footer {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 16px !important;
    margin-top: 8mm !important;
  }

  .manifest-print-footer h2,
  .manifest-print-footer h3 {
    margin: 0 !important;
    color: #000000 !important;
    font-size: 32px !important;
    font-weight: 900 !important;
  }
}
</style>
